import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMe } from '../hooks/useMe'
import { getToken } from '../lib/auth'
import { getProsConsoleSummary, type ProsConsoleSummary } from '../api/prosConsole'
import { getPlatformFeeProgram, type PlatformFeeProgram } from '../api/payouts'
import ProsOverviewTab from '../pros/console/ProsOverviewTab'
import ProsProfileTab from '../pros/console/ProsProfileTab'
import ProsServicesTab from '../pros/console/ProsServicesTab'
import ProsBookingTab from '../pros/console/ProsBookingTab'
import ProsRequestsTab from '../pros/console/ProsRequestsTab'
import ProsEarningsTab from '../pros/console/ProsEarningsTab'
import ProsPayoutsTab from '../pros/console/ProsPayoutsTab'
import ProsPlatformFeeTab from '../pros/console/ProsPlatformFeeTab'
import ProsSettingsTab from '../pros/console/ProsSettingsTab'

type TabKey = 'overview' | 'profile' | 'services' | 'booking' | 'requests' | 'earnings' | 'payouts' | 'platform_fee' | 'settings'

const TAB_ORDER: TabKey[] = ['overview', 'profile', 'services', 'booking', 'requests', 'earnings', 'payouts', 'platform_fee', 'settings']

function parseTab(value: string | null): TabKey | null {
  if (TAB_ORDER.includes((value || '') as TabKey)) return value as TabKey
  return null
}

function toLabel(tab: TabKey): string {
  if (tab === 'overview') return 'Overview'
  if (tab === 'profile') return 'Profile'
  if (tab === 'services') return 'Services'
  if (tab === 'booking') return 'Booking'
  if (tab === 'requests') return 'Requests'
  if (tab === 'earnings') return 'Earnings'
  if (tab === 'payouts') return 'Payouts'
  if (tab === 'platform_fee') return 'Platform Fee'
  if (tab === 'settings') return 'Settings'
  return 'Overview'
}

function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" aria-hidden="true">
      <path
        d="M17 10h-1V8a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7a2 2 0 0 0-2-2Zm-6 0V8a2 2 0 1 1 4 0v2h-4Z"
        fill="currentColor"
      />
    </svg>
  )
}

export default function ProsConsole() {
  const authed = Boolean(getToken())
  const { me } = useMe(authed)
  const [searchParams, setSearchParams] = useSearchParams()
  const [summary, setSummary] = useState<ProsConsoleSummary | null>(null)
  const [platformFeeProgram, setPlatformFeeProgram] = useState<PlatformFeeProgram | null>(null)
  const [profileDirty, setProfileDirty] = useState(false)
  const [pendingTab, setPendingTab] = useState<TabKey | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const requestedTab = parseTab(searchParams.get('tab'))

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const [payload, feeProgram] = await Promise.all([
        getProsConsoleSummary(),
        getPlatformFeeProgram().catch(() => null),
      ])
      setSummary(payload)
      setPlatformFeeProgram(feeProgram)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load Pro Console.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const canAccessConsole = Boolean(summary?.can_access_console)
  const availableTabs = useMemo(
    () => new Set<TabKey>(['overview', 'profile', 'services', 'booking', 'requests', 'earnings', 'payouts', 'platform_fee', 'settings']),
    []
  )

  const currentTab: TabKey = useMemo(() => {
    if (requestedTab && availableTabs.has(requestedTab)) return requestedTab
    return canAccessConsole ? 'overview' : 'profile'
  }, [availableTabs, canAccessConsole, requestedTab])
  const publicCardPath = useMemo(() => {
    const publicId = String((me as any)?.profile?.public_id || '').trim()
    return publicId ? `/people/i/${encodeURIComponent(publicId)}` : '/people'
  }, [me])

  useEffect(() => {
    if (!loading && requestedTab !== currentTab) {
      const next = new URLSearchParams(searchParams)
      next.set('tab', currentTab)
      setSearchParams(next, { replace: true })
    }
  }, [currentTab, loading, requestedTab, searchParams, setSearchParams])

  const goTab = (tab: TabKey) => {
    if (tab !== currentTab && currentTab === 'profile' && profileDirty) {
      setPendingTab(tab)
      return
    }
    const next = new URLSearchParams(searchParams)
    next.set('tab', tab)
    setSearchParams(next)
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-white/55">PIXE PRO CONSOLE</div>
              <div className="mt-1 text-2xl font-extrabold text-white">{summary?.pro.title || me?.profile?.display_name || 'Pro'}</div>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <span className={`rounded-full border px-2 py-1 ${summary?.pro.enabled ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' : 'border-amber-400/30 bg-amber-500/10 text-amber-100'}`}>
                  {summary?.pro.enabled ? 'Enabled' : 'Disabled'}
                </span>
                <span className="rounded-full border border-white/15 bg-black/30 px-2 py-1 text-white/75">Mode: {summary?.pro.mode === 'onsite' ? 'On-site' : 'Remote'}</span>
                <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-2 py-1 text-emerald-200">Joined Pro</span>
                <span className="rounded-full border border-white/15 bg-black/30 px-2 py-1 text-white/70">Staked {summary?.pro.stake_pixe || 0} PIXE</span>
                <span className={`rounded-full border px-2 py-1 ${summary?.stripe.deposits_connect_status === 'enabled' ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' : 'border-white/15 bg-black/30 text-white/70'}`}>
                  Deposits {summary?.stripe.deposits_connect_status === 'enabled' ? 'Connected' : 'Not connected'}
                </span>
                <span className={`rounded-full border px-2 py-1 ${summary?.stripe.payouts_connect_status === 'enabled' ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' : 'border-white/15 bg-black/30 text-white/70'}`}>
                  Payouts {summary?.stripe.payouts_connect_status}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link to="/pros/requests" className="rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-white hover:bg-brand-600">
                Open Requests
              </Link>
              <Link to={publicCardPath} className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10">
                View Public Card
              </Link>
              <button type="button" onClick={() => goTab('profile')} className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm hover:bg-white/10">
                Edit Profile
              </button>
            </div>
          </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-2">
        <div className="flex flex-wrap gap-2">
          {TAB_ORDER.map((tab) => {
            const locked = !availableTabs.has(tab)
            const active = currentTab === tab
            const title = locked ? 'Subscribe to unlock Overview/Earnings/Payouts' : toLabel(tab)
            return (
              <button
                key={tab}
                type="button"
                title={title}
                onClick={() => {
                  if (!locked) goTab(tab)
                }}
                disabled={locked}
                className={`rounded-xl border px-3 py-2 text-sm ${active ? 'border-brand/50 bg-brand/20 text-white' : locked ? 'cursor-not-allowed border-white/10 bg-black/20 text-white/40' : 'border-white/15 bg-white/5 text-white/80 hover:bg-white/10'}`}
              >
                <span className="inline-flex items-center gap-1.5">
                  <span>{toLabel(tab)}</span>
                  {locked ? <LockIcon /> : null}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {loading ? <div className="text-sm text-white/70">Loading console...</div> : null}
      {error ? <div className="text-sm text-red-300">{error}</div> : null}
      {!loading && currentTab === 'overview' ? <ProsOverviewTab /> : null}
      {!loading && currentTab === 'profile' ? <ProsProfileTab onDirtyChange={setProfileDirty} /> : null}
      {!loading && currentTab === 'services' ? <ProsServicesTab /> : null}
      {!loading && currentTab === 'booking' ? <ProsBookingTab /> : null}
      {!loading && currentTab === 'requests' ? <ProsRequestsTab /> : null}
      {!loading && currentTab === 'earnings' ? <ProsEarningsTab /> : null}
      {!loading && currentTab === 'payouts' ? <ProsPayoutsTab /> : null}
      {!loading && currentTab === 'platform_fee' ? <ProsPlatformFeeTab program={platformFeeProgram} /> : null}
      {!loading && currentTab === 'settings' ? (
        <ProsSettingsTab
          enabled={Boolean(summary?.pro.enabled)}
          status={{
            enabled: Boolean(summary?.pro.enabled),
            visibleInDiscovery: Boolean(summary?.visible_in_discovery),
            bookingEnabled: Boolean(summary?.booking_enabled),
            lastUpdatedAt: summary?.pro_enabled_updated_at || null,
          }}
          onChanged={() => void load()}
        />
      ) : null}

      {pendingTab ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-xl border border-white/10 bg-neutral-950 p-4">
            <div className="text-base font-semibold text-white">Discard unsaved changes?</div>
            <div className="mt-2 text-sm text-white/70">You have unsaved profile edits. Leave this tab and discard changes?</div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPendingTab(null)}
                className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white hover:bg-white/10"
              >
                Stay
              </button>
              <button
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(searchParams)
                  next.set('tab', pendingTab)
                  setSearchParams(next)
                  setPendingTab(null)
                  setProfileDirty(false)
                }}
                className="rounded-lg bg-red-500 px-3 py-2 text-sm font-semibold text-white hover:bg-red-600"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
